import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CpuChipIcon,
  CommandLineIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminLayout: React.FC = () => {
  const { logout, user } = useAuth();
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const links = [
    { to: '/admin', label: t.admin?.nav?.dashboard ?? 'Tableau de bord', icon: HomeIcon, end: true },
    { to: '/admin/subscribers', label: t.admin?.nav?.subscribers ?? 'Abonnés', icon: UsersIcon },
    { to: '/admin/invoices', label: t.admin?.nav?.invoices ?? 'Factures SaaS', icon: DocumentTextIcon },
    { to: '/admin/metrics', label: t.admin?.nav?.metrics ?? 'Métriques', icon: ChartBarIcon },
    { to: '/admin/ai-models', label: t.admin?.nav?.aiModels ?? 'Modèles IA', icon: CpuChipIcon },
    { to: '/admin/ops', label: t.admin?.nav?.ops ?? 'Ops', icon: CommandLineIcon },
  ];

  const linkClasses = (isActive: boolean) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-indigo-600 text-white shadow-sm'
        : 'text-slate-200 hover:bg-slate-700 hover:text-white'
    }`;

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={`fixed top-0 ${isRTL ? 'right-0' : 'left-0'} z-40 h-full w-64 bg-slate-900 transform transition-transform duration-300 lg:translate-x-0 lg:static lg:z-auto ${
          open ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">GB</span>
            </div>
            <div>
              <div className="text-sm font-bold text-white">GreenBill</div>
              <div className="text-xs text-indigo-300 uppercase tracking-wide">
                {t.admin?.console ?? 'Admin'}
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) => linkClasses(isActive)}
            >
              <l.icon className="h-5 w-5 flex-shrink-0" />
              <span>{l.label}</span>
            </NavLink>
          ))}

          <div className="pt-6 mt-6 border-t border-slate-700 space-y-1">
            <button
              onClick={toggleLanguage}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-200 hover:bg-slate-700 hover:text-white"
            >
              <span className="text-xs font-bold w-5 text-center">
                {language === 'fr' ? 'AR' : 'FR'}
              </span>
              <span>{language === 'fr' ? 'العربية' : 'Français'}</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-200 hover:bg-red-600 hover:text-white"
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
              <span>{t.admin?.logout ?? 'Se déconnecter'}</span>
            </button>
          </div>
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden text-slate-600 hover:text-slate-900"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="text-sm text-slate-600">
            {user?.prenom} {user?.nom}{' '}
            <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
              PLATFORM ADMIN
            </span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
